/**
 * Frontend error helpers — F11 T11.2b.
 *
 * `extractErrorCode` — wyciaga `error` field z axios error response body.
 * Backend zwraca {error: 'CODE_LIKE_THIS', ...} dla 4xx — wykorzystywane w toaster
 * mappingach + decyzjach UX (np. NIP_TAKEN -> modal).
 *
 * `extractFieldErrors` / `isValidationError` / `getFieldError` — uniwersalna obsluga
 * bledow walidacji per-pole. Backend (Zod) na 400 zwraca
 * `{ error: 'VALIDATION_ERROR', details: { <pole>: string[] } }` (ksztalt z
 * `parsed.error.flatten().fieldErrors`). Te helpery pozwalaja pokazac blad pod
 * konkretnym inputem w KAZDYM formularzu, zamiast generycznego toastu. Do stanu
 * bledow pol w komponencie uzyj hooka `useFieldErrors` (hooks/useFieldErrors.ts),
 * ktory na tych funkcjach bazuje.
 */

import { isAxiosError } from 'axios';

export function extractErrorCode(err: unknown): string | undefined {
  if (isAxiosError(err)) {
    const data = err.response?.data;
    if (data && typeof data === 'object' && 'error' in data) {
      const code = (data as { error?: unknown }).error;
      if (typeof code === 'string') return code;
    }
  }
  return undefined;
}

/**
 * Slug pola zarezerwowany przez system — z odpowiedzi serwera `{ error: 'FIELD_SLUG_RESERVED',
 * slug }`. Zwracaja go trasy, ktore buduja tabele kartoteki wlasnej z ISTNIEJACYCH pol (duplikacja
 * kartoteki, przywrocenie z kosza, instalacja i aktualizacja szablonu), gdy pole ma slug
 * zarezerwowany PO jego utworzeniu (np. `decisions`). Ten sam ksztalt oddaja tez dodanie pola
 * kartoteki wlasnej (400) i pola kartoteki systemowej w panelu ITE
 * (`POST /api/admin/kartoteki/:slug/fields` — 409), wiec status HTTP nie ma znaczenia: helper
 * sprawdza tylko kod bledu i niepusty slug. `null` = to nie ten blad (albo odpowiedz bez sluga) —
 * wolajacy pokazuje wtedy swoj zwykly komunikat bledu.
 */
export function extractReservedFieldSlug(err: unknown): string | null {
  if (!isAxiosError(err) || extractErrorCode(err) !== 'FIELD_SLUG_RESERVED') return null;
  const data: unknown = err.response?.data;
  const slug =
    typeof data === 'object' && data !== null ? (data as { slug?: unknown }).slug : undefined;
  return typeof slug === 'string' && slug.length > 0 ? slug : null;
}

/** Czy blad to serwerowy VALIDATION_ERROR (Zod odrzucil pola formularza). */
export function isValidationError(err: unknown): boolean {
  return extractErrorCode(err) === 'VALIDATION_ERROR';
}

/**
 * Mapa `pole -> komunikaty[]` z `details` odpowiedzi VALIDATION_ERROR.
 * Zwraca pusta mape gdy to nie axios/nie ma `details` — bezpieczne do wolania zawsze.
 */
export function extractFieldErrors(err: unknown): Record<string, string[]> {
  if (!isAxiosError(err)) return {};
  const data = err.response?.data;
  if (!data || typeof data !== 'object' || !('details' in data)) return {};
  const details = (data as { details?: unknown }).details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};
  const out: Record<string, string[]> = {};
  for (const [field, messages] of Object.entries(details as Record<string, unknown>)) {
    if (Array.isArray(messages)) {
      const strings = messages.filter((m): m is string => typeof m === 'string');
      if (strings.length > 0) out[field] = strings;
    }
  }
  return out;
}

/** Pierwszy komunikat bledu dla danego pola (albo undefined). */
export function getFieldError(err: unknown, field: string): string | undefined {
  return extractFieldErrors(err)[field]?.[0];
}

/**
 * Podzbior pol ZodIssue faktycznie uzywanych do zbudowania polskiego opisu bledu
 * (patrz `lib/validation-toast.ts`). Nie kopiuje calego typu Zoda — tylko to, co
 * odczytujemy z JSON-a przyslanego przez `parsed.error.issues` w odpowiedzi API.
 */
export interface ValidationIssueLike {
  code: string;
  path: Array<string | number>;
  minimum?: number;
  maximum?: number;
  type?: string;
}

/**
 * Surowe issues Zoda z odpowiedzi VALIDATION_ERROR (`parsed.error.issues`, dodane
 * obok `details` — patrz `extractFieldErrors`). Bezpieczne do wolania zawsze —
 * zwraca [] gdy to nie axios/nie ma `issues`/ksztalt nie zgadza sie z oczekiwanym.
 */
export function extractValidationIssues(err: unknown): ValidationIssueLike[] {
  if (!isAxiosError(err)) return [];
  const data = err.response?.data;
  if (!data || typeof data !== 'object' || !('issues' in data)) return [];
  const issues = (data as { issues?: unknown }).issues;
  if (!Array.isArray(issues)) return [];
  const out: ValidationIssueLike[] = [];
  for (const issue of issues) {
    if (!issue || typeof issue !== 'object') continue;
    const code = (issue as { code?: unknown }).code;
    const path = (issue as { path?: unknown }).path;
    if (typeof code !== 'string' || !Array.isArray(path)) continue;
    const validPath = path.filter(
      (p): p is string | number => typeof p === 'string' || typeof p === 'number',
    );
    if (validPath.length === 0) continue;
    const minimum = (issue as { minimum?: unknown }).minimum;
    const maximum = (issue as { maximum?: unknown }).maximum;
    // Zod 3 nazywał rodzaj wartości `type`, Zod 4 — `origin` (np. 'string' / 'number').
    const type = (issue as { type?: unknown }).type ?? (issue as { origin?: unknown }).origin;
    out.push({
      code,
      path: validPath,
      minimum: typeof minimum === 'number' ? minimum : undefined,
      maximum: typeof maximum === 'number' ? maximum : undefined,
      type: typeof type === 'string' ? type : undefined,
    });
  }
  return out;
}
